import React, { FC } from 'react';
import { Box, Typography, useMediaQuery, useTheme, Stepper, Step, StepLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';

import { selectRoom } from 'src/store/slices/booking-slice';
import { selectBasket } from 'src/store/slices/basket-slice';

import { StepEnum } from './constants';
import { styles } from './styles';
import { stepIconWrapper } from './step-icon';

import type { StepListProps } from './types';

export const StepList: FC<StepListProps> = ({ activeStep, steps }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const variant = useMediaQuery(theme.breakpoints.down('sm')) ? 'h6' : 'h4';
    const roomData = useSelector(selectRoom);
    const currentBasket = useSelector(selectBasket);
    const isRebooker = currentBasket.options.rebooker;

    const noPaymentRequired = !!(!roomData?.advance_payment_weeks || isRebooker);

    const filteredSteps = noPaymentRequired
        ? steps.filter((step) => step !== StepEnum.Payment)
        : steps;

    const getStepClass = (step: StepEnum) =>
        step === activeStep ? styles.active : styles.disabled;

    const stepKeys = {
        [StepEnum.YourDetails]: 'your_details_details_step',
        [StepEnum.Roommates]: 'your_details_roommates_step',
        [StepEnum.Agreements]: 'your_details_agreement_step',
        [StepEnum.Payment]: 'your_details_payment_step',
    };

    const stepKeysSm = {
        [StepEnum.YourDetails]: 'your_details_sm_details_step',
        [StepEnum.Roommates]: 'your_details_sm_roommates_step',
        [StepEnum.Agreements]: 'your_details_sm_agreement_step',
        [StepEnum.Payment]: 'your_details_sm_payment_step',
    };

    return (
        <Box css={styles.wrapper}>
            {/* Smaller Screens */}
            {isSmallScreen && (
                <Stepper alternativeLabel connector={<Box className="step-connector-line" />}>
                    {filteredSteps.map((step, index) => (
                        <Step
                            key={step}
                            css={css(
                                styles.stepConnector,
                                step <= activeStep && styles.stepConnectorActive
                            )}
                        >
                            <StepLabel
                                StepIconComponent={stepIconWrapper(step, activeStep, index + 1)}
                            >
                                <Typography
                                    variant={variant}
                                    css={css(getStepClass(step), styles.stepLabelText)}
                                >
                                    {t(stepKeysSm[step])}
                                </Typography>
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}

            {/* Larger Screens */}
            {!isSmallScreen &&
                filteredSteps.map((step, index) => (
                    <Typography
                        key={step}
                        variant="h4"
                        css={css(getStepClass(step), styles.stepLabelText)}
                    >
                        {index + 1}. {t(stepKeys[step])}
                    </Typography>
                ))}
        </Box>
    );
};
